/**
 * Detail + counterfactual actions for a selected Launch Calendar timeline row.
 * Anchoring (fixed/flexible) uses the same hard/soft grammar as dependencies.
 */

import { Loader2, Lock, Unlock, X } from 'lucide-react';
import type { TimePhasedPlacement } from '../../../lib/timePhasedPlanning.types';
import type { PlacementAnchoring } from '../../../lib/placementAnchoring';
import { ANCHORING_HELP, ANCHORING_LABEL } from '../../../lib/placementAnchoring';
import {
  PLACEMENT_STATUS_CLASS,
  PLACEMENT_STATUS_LABELS,
} from './timePhasedUiShared';

const CARD =
  'bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] border border-[var(--rule)] dark:border-[var(--rule)] ';

function rationaleLine(p: TimePhasedPlacement): string | null {
  const r = p.rationale ?? {};
  if (typeof r.reason === 'string' && r.reason.trim()) return r.reason;
  if (typeof r.profile === 'string' && r.profile.trim()) {
    const assumed = r.assumedProfile === true ? ' (assumed demand)' : '';
    return `Workload template: ${r.profile}${assumed}`;
  }
  return null;
}

export function LaunchPlacementInspector({
  placement,
  counterLoading,
  onClose,
  onPromote,
  onDelay,
  onSetAnchoring,
}: {
  placement: TimePhasedPlacement;
  counterLoading: boolean;
  onClose: () => void;
  onPromote: () => void;
  onDelay: () => void;
  onSetAnchoring?: (anchoring: PlacementAnchoring) => void;
}) {
  const why = rationaleLine(placement);
  const anchoring: PlacementAnchoring =
    placement.anchoring ?? placement.launchFlexibility ?? 'flexible';
  const isFixed = anchoring === 'fixed';

  return (
    <div
      className={`${CARD} p-5 border-primary-200 dark:border-primary-800/40 animate-fadeIn`}
      role="region"
      aria-label={`Selected launch: ${placement.priorityName}`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wider text-primary-700 dark:text-primary-400 mb-1">
            Selected launch
          </p>
          <h3 className="text-lg font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)] truncate">
            {placement.priorityName}
          </h3>
          {placement.owningOrg && (
            <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-0.5">
              {placement.owningOrg}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-1.5 text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--bg-raised)]/5 dark:hover:text-[var(--text-pri)]"
          aria-label="Clear selection"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="flex flex-wrap gap-1.5 mb-4">
        <span
          className={`text-xs font-medium px-2 py-0.5 rounded-full ${PLACEMENT_STATUS_CLASS[placement.placementStatus]}`}
        >
          {PLACEMENT_STATUS_LABELS[placement.placementStatus]}
        </span>
        <span
          className={`text-xs font-medium px-2 py-0.5 rounded-full ${
            placement.buildable
              ? 'bg-[var(--slate-dim)] text-[var(--text-sec)] dark:bg-[var(--slate)]/20'
              : 'bg-[var(--hover-tint)] text-[var(--text-ter)]'
          }`}
        >
          {placement.buildable ? 'Buildable' : 'Not buildable'}
        </span>
        <span
          className={`text-xs font-medium px-2 py-0.5 rounded-full ${
            placement.launchable
              ? 'bg-[var(--green-dim)] text-[var(--green)] dark:bg-[var(--green)]/20 dark:text-[var(--text-ter)]'
              : 'bg-[var(--mustard-dim)] text-[var(--mustard)] dark:bg-[var(--mustard)]/20 dark:text-[var(--mustard)]'
          }`}
        >
          {placement.launchable ? 'Launchable' : 'Not launchable'}
        </span>
        {placement.assumedProfile && (
          <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-[var(--hover-tint)] text-[var(--text-sec)]">
            Assumed demand
          </span>
        )}
        <span
          className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
            isFixed
              ? 'bg-[var(--bg-raised)] text-white'
              : 'bg-[var(--slate-dim)] text-[var(--text-sec)] dark:bg-[var(--slate)]/30'
          }`}
          title={ANCHORING_HELP[anchoring]}
        >
          {isFixed ? <Lock className="h-3 w-3" /> : <Unlock className="h-3 w-3" />}
          {ANCHORING_LABEL[anchoring]}
          {placement.slackWeeks ? ` · ${placement.slackWeeks}w slack` : ''}
        </span>
        {placement.calendarConfidence != null && (
          <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-[var(--slate-dim)] text-[var(--text-sec)] dark:bg-[var(--slate)]/30">
            {(placement.calendarConfidence * 100).toFixed(0)}% confidence
          </span>
        )}
      </div>

      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm mb-4">
        <div>
          <dt className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">Feasible window</dt>
          <dd className="text-[var(--text-pri)] dark:text-[var(--text-pri)] font-mono text-base">
            {placement.feasibleStart && placement.feasibleEnd
              ? `${placement.feasibleStart} → ${placement.feasibleEnd}`
              : 'None'}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">Target launch</dt>
          <dd className="text-[var(--text-pri)] dark:text-[var(--text-pri)] font-mono text-base">
            {placement.targetLaunchEnd ?? placement.targetLaunchStart ?? '—'}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">Slip</dt>
          <dd
            className={
              placement.slipWeeks && placement.slipWeeks > 0
                ? 'text-[var(--mustard)] dark:text-[var(--mustard)] font-medium'
                : 'text-[var(--text-pri)] dark:text-[var(--text-pri)]'
            }
          >
            {placement.slipWeeks && placement.slipWeeks > 0
              ? `+${placement.slipWeeks} weeks`
              : 'On target'}
          </dd>
        </div>
        {why && (
          <div className="sm:col-span-2">
            <dt className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">Why this placement</dt>
            <dd className="text-[var(--text-sec)] dark:text-[var(--text-sec)]">{why}</dd>
          </div>
        )}
      </dl>

      {onSetAnchoring && (
        <div className="border-t border-[var(--rule)] dark:border-[var(--rule)] pt-4 mb-4">
          <p className="text-xs font-semibold uppercase tracking-wider text-[var(--text-ter)] dark:text-[var(--text-ter)] mb-2">
            Anchoring (hard vs soft)
          </p>
          <p className="text-xs text-[var(--text-ter)] mb-2">{ANCHORING_HELP[anchoring]}</p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={counterLoading || isFixed}
              onClick={() => onSetAnchoring('fixed')}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium bg-[var(--bg-raised)] text-white hover:bg-[var(--bg)] disabled:opacity-50"
            >
              <Lock className="h-3.5 w-3.5" />
              Lock fixed
            </button>
            <button
              type="button"
              disabled={counterLoading || !isFixed}
              onClick={() => onSetAnchoring('flexible')}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium border border-[var(--rule)] dark:border-[var(--rule)] text-[var(--text-pri)] dark:text-[var(--text-pri)] hover:bg-[var(--surface)] dark:hover:bg-[var(--bg-raised)]/[0.03] disabled:opacity-50"
              title="Unlock required before the engine may slide this date"
            >
              <Unlock className="h-3.5 w-3.5" />
              Unlock flexible
            </button>
          </div>
        </div>
      )}

      <div className="border-t border-[var(--rule)] dark:border-[var(--rule)] pt-4">
        <p className="text-xs font-semibold uppercase tracking-wider text-[var(--text-ter)] dark:text-[var(--text-ter)] mb-2">
          Say yes to this one — what happens?
        </p>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            disabled={counterLoading}
            onClick={onPromote}
            className="px-3 py-2 text-sm font-medium bg-primary-600 hover:bg-primary-700 text-white disabled:opacity-50"
          >
            Promote to top
          </button>
          <button
            type="button"
            disabled={counterLoading || isFixed}
            onClick={onDelay}
            title={
              isFixed
                ? 'Fixed (hard) launches cannot slide — unlock first, or add supply'
                : 'Shift this launch 4 weeks later (soft)'
            }
            className="px-3 py-2 text-sm font-medium border border-[var(--rule)] dark:border-[var(--rule)] text-[var(--text-pri)] dark:text-[var(--text-pri)] hover:bg-[var(--surface)] dark:hover:bg-[var(--bg-raised)]/[0.03] disabled:opacity-50"
          >
            Delay 4 weeks
          </button>
          {counterLoading && (
            <Loader2 className="h-5 w-5 animate-spin text-[var(--text-ter)] self-center" />
          )}
        </div>
      </div>
    </div>
  );
}
